import {Store} from 'vuex';

/**
 * Sync of notification pop-ups between several Vanilla tabs of the same browser.
 *
 * - A pop-up that is hidden in a visible tab (read, deleted, opened, closed, timer, panel opened …) is hidden in all
 *   other tabs too: BroadcastChannel `vanilla-notifications`, fallback the `storage` event of localStorage.
 * - The hidden ids are remembered for a while, so a tab that receives the notification later (catch-up after a
 *   reconnect, slightly delayed socket event) does not show it again.
 * - Sound and browser notification of a new notification are played by one tab only: the first tab that claims the
 *   id in localStorage wins (focused tab first, hidden tabs a bit later), the claim expires after a short TTL.
 * - Ids whose sound was played are remembered longer: a tab that catches up after a reconnect does not ring again.
 * - Anruf-Fenster: a call closed in one tab (protocol saved, dismissed, „Später“) is closed in all other tabs too and
 *   is not opened again by a late call event.
 *
 * Read/delete state itself is synced by the server (socket events MARK_READ / DISMISS reach every tab).
 * Without BroadcastChannel/localStorage every tab behaves as before (shows and plays everything).
 */

const CHANNEL_NAME = 'vanilla-notifications';
const PREFIX = 'vanilla-notifications:';
const MESSAGE_KEY = `${PREFIX}message`;
const SEEN_KEY = `${PREFIX}seen`;
const ALERTED_KEY = `${PREFIX}alerted`;
const CALLS_CLOSED_KEY = `${PREFIX}calls-closed`;
const CLAIM_PREFIX = `${PREFIX}claim:`;
/** How long hidden ids are remembered for late deliveries */
const SEEN_TTL_MS = 10 * 60 * 1000;
const SEEN_MAX = 300;
/** How long played sounds are remembered (a background tab can be disconnected for a long time) */
const ALERTED_TTL_MS = 12 * 60 * 60 * 1000;
/** How long closed calls are remembered (late or repeated call events of slow tabs) */
const CALLS_CLOSED_TTL_MS = 30 * 60 * 1000;
/** How long a claim for sound/browser notification blocks the other tabs */
const CLAIM_TTL_MS = 30 * 1000;
/** Time between writing a claim and checking that it was not overwritten by another tab */
const CLAIM_CONFIRM_MS = 40;

type HideMessage = { kind: 'hide'; ids: string[]; tab: string; at: number };
type CallClosedMessage = { kind: 'call-closed'; ids: string[]; tab: string; at: number };
type TabMessage = HideMessage | CallClosedMessage;
type Claim = { tab: string; at: number };

const tabId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

let initialized = false;
let listening = false;
let channel: BroadcastChannel | null = null;
let applyRemoteHide: ((ids: string[]) => void) | undefined;
const callClosedListeners = new Set<(callIds: string[]) => void>();
let pendingHide: string[] = [];
let flushTimer = 0;

const wait = (ms: number): Promise<void> => new Promise((resolve) => window.setTimeout(resolve, ms));

function storage(): Storage | null {
    try {
        return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
    } catch (e) {
        // e.g. blocked site data
        return null;
    }
}

function readJson<T>(key: string): T | undefined {
    try {
        const raw = storage()?.getItem(key);
        return raw ? JSON.parse(raw) as T : undefined;
    } catch (e) {
        return undefined;
    }
}

function writeJson(key: string, value: unknown): boolean {
    try {
        const store = storage();
        if (!store) {
            return false;
        }
        store.setItem(key, JSON.stringify(value));
        return true;
    } catch (e) {
        // quota or blocked storage
        return false;
    }
}

const isVisible = (): boolean => typeof document === 'undefined' || document.visibilityState !== 'hidden';

/** Remembered ids (id → time) of one key, expired ones removed */
function readIds(key: string, ttlMs: number, now = Date.now()): Record<string, number> {
    const stored = readJson<Record<string, number>>(key);
    const fresh: Record<string, number> = {};
    if (!stored || typeof stored !== 'object') {
        return fresh;
    }
    Object.keys(stored)
        .filter((id) => typeof stored[id] === 'number' && now - stored[id] < ttlMs)
        .sort((a, b) => stored[b] - stored[a])
        .slice(0, SEEN_MAX)
        .forEach((id) => {
            fresh[id] = stored[id];
        });
    return fresh;
}

function rememberIds(key: string, ttlMs: number, ids: string[]): void {
    const now = Date.now();
    const stored = readIds(key, ttlMs, now);
    ids.forEach((id) => {
        stored[id] = now;
    });
    writeJson(key, stored);
}

function wasRemembered(key: string, ttlMs: number, id: string, maxAgeMs = ttlMs): boolean {
    const at = readIds(key, ttlMs)[String(id)];
    return typeof at === 'number' && Date.now() - at < Math.min(maxAgeMs, ttlMs);
}

/** True when the pop-up of this id was hidden in a visible tab within `maxAgeMs` (default: the whole TTL) */
export function wasSeenInAnyTab(id: string, maxAgeMs = SEEN_TTL_MS): boolean {
    return wasRemembered(SEEN_KEY, SEEN_TTL_MS, id, maxAgeMs);
}

/** Notes that the sound of this notification was played (in this tab, after winning the claim) */
export function rememberAlerted(id: string): void {
    rememberIds(ALERTED_KEY, ALERTED_TTL_MS, [String(id)]);
}

/** True when any tab already played the sound of this notification */
export function wasAlertedInAnyTab(id: string): boolean {
    return wasRemembered(ALERTED_KEY, ALERTED_TTL_MS, id);
}

/** True when the Anruf-Fenster of this call was closed in any tab */
export function wasCallClosedInAnyTab(callId: string): boolean {
    return wasRemembered(CALLS_CLOSED_KEY, CALLS_CLOSED_TTL_MS, callId);
}

function isTabMessage(data: unknown): data is TabMessage {
    const message = data as TabMessage;
    return !!message && typeof message === 'object' && (message.kind === 'hide' || message.kind === 'call-closed')
        && Array.isArray(message.ids) && typeof message.tab === 'string';
}

function onMessage(data: unknown): void {
    if (!isTabMessage(data) || data.tab === tabId) {
        return;
    }
    const ids = data.ids.map(String).filter(Boolean);
    if (!ids.length) {
        return;
    }
    if (data.kind === 'hide') {
        applyRemoteHide?.(ids);
        return;
    }
    callClosedListeners.forEach((listener) => {
        try {
            listener(ids);
        } catch (e) {
            // one broken listener must not stop the others
        }
    });
}

function post(kind: TabMessage['kind'], ids: string[]): void {
    const message: TabMessage = {kind, ids, tab: tabId, at: Date.now()};
    if (channel) {
        try {
            channel.postMessage(message);
            return;
        } catch (e) {
            // closed channel: fall back to localStorage
        }
    }
    // the storage event only fires on a changed value: `at` and `tab` make every message unique
    writeJson(MESSAGE_KEY, message);
}

function flushHidden(): void {
    flushTimer = 0;
    const ids = Array.from(new Set(pendingHide));
    pendingHide = [];
    if (!ids.length) {
        return;
    }
    rememberIds(SEEN_KEY, SEEN_TTL_MS, ids);
    post('hide', ids);
}

/** Tells the other tabs that these pop-ups were hidden here (only from a visible tab: the user could see them) */
export function broadcastHidden(ids: unknown[]): void {
    if (!isVisible()) {
        return;
    }
    pendingHide.push(...ids.map(String).filter(Boolean));
    if (!flushTimer) {
        flushTimer = window.setTimeout(flushHidden, 0);
    }
}

function claimDelay(): number {
    if (typeof document === 'undefined') {
        return 0;
    }
    if (document.hasFocus()) {
        return 0;
    }
    return isVisible() ? 80 : 200 + Math.floor(Math.random() * 150);
}

function pruneClaims(now: number): void {
    try {
        const store = storage();
        if (!store) {
            return;
        }
        for (let i = store.length - 1; i >= 0; i--) {
            const key = store.key(i);
            if (!key || !key.startsWith(CLAIM_PREFIX)) {
                continue;
            }
            const claim = readJson<Claim>(key);
            if (!claim || typeof claim.at !== 'number' || now - claim.at > CLAIM_TTL_MS) {
                store.removeItem(key);
            }
        }
    } catch (e) {
        // nothing to clean up then
    }
}

/**
 * Resolves true in exactly one tab per key (as far as localStorage allows): that tab plays the sound and shows the
 * browser notification. Resolves true when localStorage is not available (behaviour as before).
 */
export async function claimAlert(key: string): Promise<boolean> {
    if (!storage()) {
        return true;
    }
    await wait(claimDelay());
    const storageKey = `${CLAIM_PREFIX}${key}`;
    const now = Date.now();
    const current = readJson<Claim>(storageKey);
    if (current && current.tab !== tabId && typeof current.at === 'number' && now - current.at < CLAIM_TTL_MS) {
        return false;
    }
    if (!writeJson(storageKey, {tab: tabId, at: now})) {
        return true;
    }
    await wait(CLAIM_CONFIRM_MS);
    const confirmed = readJson<Claim>(storageKey);
    pruneClaims(now);
    return !confirmed || confirmed.tab === tabId;
}

/**
 * Anruf-Fenster: tells the other tabs that these calls were closed here (protocol saved, dismissed, „Später“) and
 * remembers them, so late call events do not open them again. Not announced back to this tab.
 */
export function broadcastCallClosed(callIds: unknown[]): void {
    const ids = Array.from(new Set(callIds.map(String).filter(Boolean)));
    if (!ids.length || typeof window === 'undefined') {
        return;
    }
    listen();
    rememberIds(CALLS_CLOSED_KEY, CALLS_CLOSED_TTL_MS, ids);
    post('call-closed', ids);
}

/** Anruf-Fenster: calls closed in another tab; returns the function that removes the listener */
export function onCallClosedInOtherTab(listener: (callIds: string[]) => void): () => void {
    listen();
    callClosedListeners.add(listener);
    return () => callClosedListeners.delete(listener);
}

/** Opens the channel and the storage listener once per page view */
function listen(): void {
    if (listening || typeof window === 'undefined') {
        return;
    }
    listening = true;
    try {
        if (typeof BroadcastChannel !== 'undefined') {
            channel = new BroadcastChannel(CHANNEL_NAME);
            channel.onmessage = (event: MessageEvent) => onMessage(event.data);
        }
    } catch (e) {
        channel = null;
    }
    window.addEventListener('storage', (event: StorageEvent) => {
        if (event.key !== MESSAGE_KEY || !event.newValue) {
            return;
        }
        try {
            onMessage(JSON.parse(event.newValue));
        } catch (e) {
            // foreign or broken value
        }
    });
}

/**
 * Starts the sync once per page view. Every `hideNotification` of the store is announced to the other tabs; hide
 * messages of other tabs are applied with `hidePopups` (not announced again).
 */
export function initNotificationTabSync(store: Store<any>): void {
    if (initialized || typeof window === 'undefined') {
        return;
    }
    initialized = true;
    applyRemoteHide = (ids) => store.commit('hidePopups', ids);
    store.subscribe((mutation) => {
        if (mutation.type === 'hideNotification' && mutation.payload) {
            broadcastHidden([mutation.payload]);
        }
    });
    listen();
}
